import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Upload, FileText, Users, Package, History, CheckCircle, AlertCircle, Trash2 } from 'lucide-react';

const SOURCES = [
  { id: 'fortnox', label: 'Fortnox' },
  { id: 'bokio', label: 'Bokio' },
  { id: 'visma', label: 'Visma' },
  { id: 'other', label: 'Annan' },
];

export default function DataImportPage() {
  const [source, setSource] = useState('fortnox');
  const [importType, setImportType] = useState('invoices');
  const [importing, setImporting] = useState(false);
  const [history, setHistory] = useState(null);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);  // Iter48: preview före import
  const [pendingFile, setPendingFile] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.getImportHistory().then(r => setHistory(r.data)).catch(() => {});
  }, []);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setResult(null);
    setPreview(null);

    // Iter48: För kunder — kör alltid preview först (SIE4/Excel/CSV/JSON smart-detect)
    if (importType === 'customers') {
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('entity', 'customers');
        const res = await api.previewImport(fd);
        setPreview(res.data);
        setPendingFile(file);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Kunde inte läsa filen');
      } finally {
        setImporting(false);
      }
      return;
    }

    // Övriga typer — enkel import (som tidigare)
    const fd = new FormData();
    fd.append('file', file);
    fd.append('source', source);
    try {
      let res;
      if (importType === 'invoices') res = await api.importInvoices(fd);
      else res = await api.importArticles(fd);
      setResult(res.data);
      toast.success(res.data.message);
      api.getImportHistory().then(r => setHistory(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import misslyckades');
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleConfirmImport = async () => {
    if (!pendingFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', pendingFile);
      fd.append('entity', 'customers');
      fd.append('source', source);
      const res = await api.universalImport(fd);
      const msg = `${res.data.count} nya kunder importerade${res.data.skipped_duplicates > 0 ? ` · ${res.data.skipped_duplicates} dubbletter hoppades över` : ''}`;
      setResult({ message: msg, count: res.data.count });
      toast.success(msg);
      setPreview(null);
      setPendingFile(null);
      api.getImportHistory().then(r => setHistory(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import misslyckades');
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleCancelPreview = () => {
    setPreview(null);
    setPendingFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleCleanupEmpties = async () => {
    if (!window.confirm('Rensa alla tomma importer (kunder, fakturor & artiklar utan innehåll)?\n\nDetta går inte att ångra.')) return;
    try {
      const res = await api.cleanupEmptyImports();
      toast.success(res.data.message);
      api.getImportHistory().then(r => setHistory(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte rensa');
    }
  };

  const handleDedupeCustomers = async () => {
    if (!window.confirm(
      'Ta bort dubbletter av kunder?\n\nBehåller den äldsta posten per kundnummer / orgnr / e-post / namn+ort. Går inte att ångra.'
    )) return;
    try {
      const res = await api.dedupeImportedCustomers();
      toast.success(res.data.message);
      api.getImportHistory().then(r => setHistory(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte rensa dubbletter');
    }
  };

  return (
    <div className="space-y-6" data-testid="data-import-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Dataimport</h1>
        <p className="text-sm text-muted-foreground mt-1">Importera fakturor, kunder och artiklar från Fortnox, Bokio eller andra system</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Import panel */}
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-6 space-y-5">
          <h2 className="text-base font-bold">Välj källa och typ</h2>

          {/* Source */}
          <div className="flex flex-wrap gap-2">
            {SOURCES.map(s => (
              <button
                key={s.id}
                onClick={() => setSource(s.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${source === s.id ? 'bg-[#E8613A] text-white border-[#E8613A]' : 'bg-background border-border text-foreground hover:bg-muted'}`}
                data-testid={`source-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Type */}
          <div className="flex gap-3">
            {[
              { id: 'invoices', label: 'Fakturor', icon: FileText },
              { id: 'customers', label: 'Kunder', icon: Users },
              { id: 'articles', label: 'Artiklar', icon: Package },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setImportType(t.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium flex-1 transition-colors ${importType === t.id ? 'border-[#E8613A] bg-[#E8613A]/5 text-[#E8613A]' : 'border-border bg-background text-muted-foreground hover:bg-muted'}`}
                data-testid={`type-${t.id}`}
              >
                <t.icon size={18} />
                {t.label}
              </button>
            ))}
          </div>

          {/* File format info */}
          <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Filformat som stöds:</p>
            <ul className="list-disc pl-5 space-y-0.5">
              <li><strong>SIE4</strong> (.si, .se, .sie) — Fortnox/Bokio full-export (rekommenderad)</li>
              <li><strong>Excel</strong> (.xlsx) — direktexport från Fortnox/Bokio/Visma</li>
              <li><strong>CSV</strong> — Semikolon-separerad (;), UTF-8</li>
              <li><strong>JSON</strong> — Array av objekt, eller objekt med nyckel "{importType}"</li>
            </ul>
            <p className="mt-2">Fält mappas automatiskt (svenska &amp; engelska kolumnnamn stöds). För kunder visas en förhandsvisning innan importen körs.</p>
          </div>

          {/* Upload */}
          <div>
            <input ref={fileRef} type="file" accept=".csv,.json,.xlsx,.xls,.si,.se,.sie" onChange={handleImport} className="hidden" data-testid="import-file-input" />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={importing || !!preview}
              className="bg-[#E8613A] hover:bg-[#d4552f] text-white w-full py-6 text-base"
              data-testid="import-btn"
            >
              <Upload size={18} className="mr-2" />
              {importing ? 'Läser fil...' : `Ladda upp ${importType === 'invoices' ? 'fakturor' : importType === 'customers' ? 'kunder' : 'artiklar'}`}
            </Button>
          </div>

          {/* Preview (Iter48) — visas för kunder innan import */}
          {preview && (
            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-300 dark:border-blue-700 rounded-lg p-4" data-testid="import-preview">
              <p className="font-bold text-foreground mb-2 text-sm">Förhandsvisning ({preview.file_type})</p>
              <div className="grid grid-cols-3 gap-3 my-3">
                <div className="text-center p-2 bg-background rounded border border-border">
                  <p className="text-2xl font-bold text-blue-600">{preview.total_parsed}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Rader i fil</p>
                </div>
                <div className="text-center p-2 bg-background rounded border border-green-300">
                  <p className="text-2xl font-bold text-green-600">{preview.new_count}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Nya kunder</p>
                </div>
                <div className="text-center p-2 bg-background rounded border border-amber-300">
                  <p className="text-2xl font-bold text-amber-600">{preview.duplicate_count}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Dubbletter</p>
                </div>
              </div>
              {preview.sample_new?.length > 0 && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Visa exempel på nya rader ({preview.sample_new.length})
                  </summary>
                  <ul className="mt-2 space-y-1 pl-4 list-disc text-muted-foreground">
                    {preview.sample_new.map((r, i) => (
                      <li key={i}>
                        {r.name || r.company_name || '—'}
                        {r.customer_number && <span className="text-blue-600 ml-1">#{r.customer_number}</span>}
                        {r.city && <span className="ml-1">· {r.city}</span>}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              <div className="flex gap-2 mt-4">
                <Button
                  onClick={handleConfirmImport}
                  disabled={importing || preview.new_count === 0}
                  className="flex-1 bg-[#E8613A] hover:bg-[#d4552f] text-white"
                  data-testid="import-confirm-btn"
                >
                  {importing ? 'Importerar...' : `Importera ${preview.new_count} kunder`}
                </Button>
                <Button
                  onClick={handleCancelPreview}
                  variant="outline"
                  disabled={importing}
                  data-testid="import-cancel-btn"
                >
                  Avbryt
                </Button>
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="bg-green-50 dark:bg-green-950/30 border border-green-300 dark:border-green-700 rounded-lg p-4" data-testid="import-result">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={18} className="text-green-600" />
                <span className="font-medium text-green-700 dark:text-green-300">{result.message}</span>
              </div>
              {result.invoices && (
                <div className="mt-2 max-h-48 overflow-y-auto text-sm">
                  {result.invoices.slice(0, 5).map((inv, i) => (
                    <div key={i} className="flex justify-between py-1 border-b border-green-200 dark:border-green-800 last:border-0">
                      <span>{inv.invoice_number || '—'} — {inv.customer_name}</span>
                      <span className="font-medium">{inv.total_amount?.toLocaleString('sv-SE')} kr</span>
                    </div>
                  ))}
                  {result.invoices.length > 5 && <p className="text-xs text-muted-foreground mt-1">...och {result.invoices.length - 5} till</p>}
                </div>
              )}
            </div>
          )}
        </div>

        {/* History sidebar */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h2 className="text-base font-bold flex items-center gap-2 mb-4">
            <History size={16} className="text-[#E8613A]" /> Importhistorik
          </h2>
          {history ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-sm text-muted-foreground flex items-center gap-2"><FileText size={14} /> Fakturor</span>
                <span className="text-sm font-bold">{history.invoice_count}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-sm text-muted-foreground flex items-center gap-2"><Users size={14} /> Kunder</span>
                <span className="text-sm font-bold">{history.customer_count}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-sm text-muted-foreground flex items-center gap-2"><Package size={14} /> Artiklar</span>
                <span className="text-sm font-bold">{history.article_count}</span>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={handleCleanupEmpties}
                data-testid="cleanup-empties-btn"
                className="w-full mt-2 text-xs border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950"
              >
                <Trash2 size={12} className="mr-1.5" /> Rensa tomma importer
              </Button>
              <p className="text-[10px] text-muted-foreground leading-tight">
                Tar bort alla importerade rader som saknar innehåll (t.ex. tomma CSV-rader).
              </p>

              <Button
                variant="outline"
                size="sm"
                onClick={handleDedupeCustomers}
                data-testid="dedupe-customers-btn"
                className="w-full mt-2 text-xs border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-900 dark:hover:bg-amber-950"
              >
                <Users size={12} className="mr-1.5" /> Ta bort dubbletter av kunder
              </Button>
              <p className="text-[10px] text-muted-foreground leading-tight">
                Behåller den äldsta posten per kundnummer, orgnr, e-post eller namn+ort. Rekommenderat om samma fil importerats flera gånger.
              </p>
              {history.recent_invoices?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-muted-foreground uppercase mb-2">Senaste importer</p>
                  {history.recent_invoices.slice(0, 5).map((inv, i) => (
                    <div key={i} className="text-xs py-1 flex justify-between">
                      <span className="truncate mr-2">{inv.customer_name || inv.invoice_number}</span>
                      <span>{inv.total_amount?.toLocaleString('sv-SE')} kr</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Laddar...</p>
          )}
        </div>
      </div>
    </div>
  );
}
